import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Header, Footer } from "../components/shared";
import HeroOther from "../components/HeroOther/HeroOther";
import ServiceHero from "../components/ServiceHero/ServiceHero";
import serviceHeroData from "../components/ServiceHero/serviceHeroData";
import ServiceChallenges from "../components/ServiceChallenges/ServiceChallenges";
import servicesData from "../../data/servicesData.json";
import ServiceFAQs from "../components/ServiceFAQs/ServiceFAQs";
import { getFAQServiceName } from "../components/ServiceFAQs/serviceFAQMapping";
import { useRegion } from "../../regions/RegionContext";
import {
  CheckCircle,
  Users,
  Lightbulb,
  Award,
  ArrowRight,
  MessageCircle,
  Phone,
  Sparkles,
  TrendingUp,
  Zap,
  Shield
} from "lucide-react";

// Per-service badge text shown on the featured benefit card
const serviceBadges = {
  "process-optimization":  { label: "Operational Edge",    sub: "Eliminate Bottlenecks" },
  "menu-engineering":      { label: "Profit Driver",        sub: "Maximise Every Cover" },
  "kitchen-operations":    { label: "Kitchen First",        sub: "Consistency at Scale" },
  "inventory-management":  { label: "Zero Waste",           sub: "Stock Smart" },
  "branding-marketing":    { label: "Brand Builder",        sub: "Stand Out & Win" },
  "digital-profiling":     { label: "Digital Presence",     sub: "Own Your Online Story" },
  "delivery-platforms":    { label: "Delivery Growth",      sub: "Dominate Swiggy & Zomato" },
  "catering-events":       { label: "Revenue Multiplier",   sub: "Events That Pay Off" },
  "crm":                   { label: "Guest Loyalty",        sub: "Retain & Delight" },
  "financial-accounts":    { label: "Financial Clarity",    sub: "Know Your Numbers" },
  "employee-management":   { label: "Team Excellence",      sub: "People Who Perform" },
  "infrastructure-management": { label: "Built to Last",   sub: "Solid Foundations" },
};

// Challenge section images (4:3) — all verified
const serviceImages = {
  "process-optimization":      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",  // open-plan cafe interior
  "menu-engineering":          "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&h=600&fit=crop",  // juicy burger on board
  "kitchen-operations":        "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=800&h=600&fit=crop",  // chef cooking with flame
  "inventory-management":      "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop",  // colourful vegetable produce
  "branding-marketing":        "https://images.unsplash.com/photo-1493770348161-369560ae357d?w=800&h=600&fit=crop",  // breakfast food flat lay
  "digital-profiling":         "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",  // laptop analytics charts
  "delivery-platforms":        "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=800&h=600&fit=crop",  // Indian chicken curry & naan
  "catering-events":           "https://images.unsplash.com/photo-1555244162-803834f70033?w=800&h=600&fit=crop",  // catering buffet with chafing dishes
  "crm":                       "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop",  // upscale dark restaurant interior
  "financial-accounts":        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop",  // tax forms & calculator
  "employee-management":       "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=800&h=600&fit=crop",  // large restaurant with bar
  "infrastructure-management": "https://images.unsplash.com/photo-1544148103-0773bf10d330?w=800&h=600&fit=crop",  // dining room with menus
};

// Featured benefit card images (1:1) — all verified, no third-party branding
const benefitImages = {
  "process-optimization":      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=800&fit=crop",  // fine dining plate & wine
  "menu-engineering":          "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800&h=800&fit=crop",  // Indian curry dishes overhead
  "kitchen-operations":        "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=800&h=800&fit=crop",  // plated salmon fine dining
  "inventory-management":      "https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=800&h=800&fit=crop",  // colourful food display
  "branding-marketing":        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=800&fit=crop",  // overhead plated meat dishes
  "digital-profiling":         "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800&h=800&fit=crop",  // mobile/social media
  "delivery-platforms":        "https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=800&h=800&fit=crop",  // gourmet burger close-up
  "catering-events":           "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=800&h=800&fit=crop",  // salad bowl aesthetic
  "crm":                       "https://images.unsplash.com/photo-1567521464027-f127ff144326?w=800&h=800&fit=crop",  // bright modern restaurant
  "financial-accounts":        "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&h=800&fit=crop",  // tax doc & calculator overhead
  "employee-management":       "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&h=800&fit=crop",  // modern white kitchen
  "infrastructure-management": "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=800&fit=crop",  // cafe with wooden tables
};

// Who needs this section images (portrait 4:5) — all verified
const whoNeedsImages = {
  "process-optimization":      "https://images.unsplash.com/photo-1581349485608-9469926a8e5e?w=800&h=1000&fit=crop",  // chef plating at pass
  "menu-engineering":          "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&h=1000&fit=crop",  // burger on wooden board
  "kitchen-operations":        "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=1000&fit=crop",  // fine dining plated dish
  "inventory-management":      "https://images.unsplash.com/photo-1493770348161-369560ae357d?w=800&h=1000&fit=crop",  // breakfast flat lay spread
  "branding-marketing":        "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=1000&fit=crop",  // fresh colourful produce
  "digital-profiling":         "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=1000&fit=crop",  // two hands at laptop
  "delivery-platforms":        "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=1000&fit=crop",  // plated food overhead
  "catering-events":           "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=800&h=1000&fit=crop",  // plated salmon & wine
  "crm":                       "https://images.unsplash.com/photo-1544148103-0773bf10d330?w=800&h=1000&fit=crop",  // dining room with menus
  "financial-accounts":        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=1000&fit=crop",  // analytics charts laptop
  "employee-management":       "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=800&h=1000&fit=crop",  // chef with flame in kitchen
  "infrastructure-management": "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=800&h=1000&fit=crop",  // large restaurant interior
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const { config } = useRegion();
  const [service, setService] = useState(null);
  const [faqServiceName, setFaqServiceName] = useState(null);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  useEffect(() => {
    const serviceData = servicesData[serviceId];
    if (serviceData) {
      setService(serviceData);
      // Get the corresponding FAQ service name
      const faqName = getFAQServiceName(serviceId);
      setFaqServiceName(faqName);
    }
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#F6F4EF]">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {serviceHeroData[serviceId] ? (
        <ServiceHero serviceId={serviceId} hero={service.hero} />
      ) : (
        <HeroOther item={service.hero} />
      )}

      {/* Industrial Challenges Section — redesigned, brand-themed, auto-scrolling */}
      <ServiceChallenges service={service} image={serviceImages[serviceId]} />

      {/* Why Need This - Enhanced Magazine Layout with Screenshot Design */}
      <section className="relative py-20 md:py-24 px-6 md:px-8 lg:px-12 bg-gradient-to-b from-white to-[#F6F4EF]">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 md:mb-16"
          >
            <div className="inline-block bg-primary/10 rounded-full mb-4">
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Why Choose Us</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-[1.1]"
              style={{ letterSpacing: '-0.02em' }}
            >
              {service.whyNeedThis.title}
            </h2>
          </motion.div>

          {/* Featured Benefit - Large Hero Card with Image */}
          {service.whyNeedThis.benefits[0] && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8 md:mb-12 relative group"
            >
              <div className="relative bg-gradient-to-br from-primary via-[#1d8547] to-primary-dark rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500">
                {/* Pattern Overlay */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                      backgroundSize: '32px 32px'
                    }}
                  />
                </div>

                {/* Animated Gradient Overlay */}
                <motion.div
                  animate={{ 
                    backgroundPosition: ['0% 0%', '100% 100%'],
                  }}
                  transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(252,211,77,0.2) 0%, transparent 50%)',
                    backgroundSize: '200% 200%'
                  }}
                />

                <div className="relative grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-14 items-center p-8 md:p-10 lg:p-12">
                  {/* Left: Content */}
                  <div className="text-white flex flex-col justify-center">
                    <div className="text-5xl md:text-6xl mb-5">
                      {service.whyNeedThis.benefits[0].icon}
                    </div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold mb-4 text-white leading-[1.1]">
                      {service.whyNeedThis.benefits[0].title}
                    </h3>
                    <p className="text-base md:text-lg lg:text-xl text-white/95 leading-relaxed">
                      {service.whyNeedThis.benefits[0].description}
                    </p>

                    {/* Decorative Line */}
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '120px' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="h-1 bg-white/50 rounded-full mt-8"
                    />
                  </div>

                  {/* Right: Image — clean, crisp, always filled */}
                  <div className="hidden lg:block relative self-center">
                    <motion.div
                      animate={{ y: [0, -14, 0] }}
                      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-white/25 shadow-2xl ring-1 ring-black/5"
                    >
                      <img
                        src={benefitImages[serviceId] || "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=800&fit=crop"}
                        alt={service.whyNeedThis.benefits[0].title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Subtle bottom gradient only — keeps the photo crisp */}
                      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/25 to-transparent" />
                    </motion.div>

                    {/* Floating Badge */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                      className="absolute -bottom-5 -right-5 bg-white rounded-2xl px-5 py-3.5 shadow-2xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-5 h-5 text-primary" strokeWidth={2.5} />
                        </div>
                        <div>
                          <div className="text-lg font-bold text-gray-900 leading-tight">{(serviceBadges[serviceId] || { label: "Top Priority" }).label}</div>
                          <div className="text-sm text-gray-600">{serviceId === "delivery-platforms" ? `Dominate ${config.terminology.deliveryPlatforms}` : (serviceBadges[serviceId] || { sub: "Essential Service" }).sub}</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Remaining Benefits - Card Grid with Black Text */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.whyNeedThis.benefits.slice(1).map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative"
              >
                <div className="h-full p-8 bg-white rounded-2xl border-2 border-gray-100 hover:border-primary/30 transition-all duration-300 shadow-md hover:shadow-2xl">
                  {/* Icon */}
                  <motion.div 
                    className="text-5xl md:text-6xl mb-6"
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {benefit.icon}
                  </motion.div>

                  {/* Content - Black Text */}
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Hover Effect - Bottom Accent */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary to-primary-dark rounded-b-2xl"
                    initial={{ width: '0%' }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  />

                  {/* Corner Decoration */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/5 to-transparent rounded-tr-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This - Side by Side with Better Alignment */}
      <section className="relative py-20 md:py-24 px-6 md:px-8 lg:px-12 bg-[#F6F4EF]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="inline-block bg-primary/10 rounded-full mb-6">
                <span className="text-sm font-bold text-primary uppercase tracking-wider">Perfect For</span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-10 leading-[1.1]"
                style={{ letterSpacing: '-0.02em' }}
              >
                {service.whoNeedsThis.title}
              </h2>

              <div className="space-y-8">
                {service.whoNeedsThis.targetAudience.map((audience, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group flex gap-5"
                  >
                    <div className="flex-shrink-0">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className="w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg"
                      >
                        <CheckCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                      </motion.div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                        {audience.type}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-base md:text-lg">
                        {audience.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Image with Floating Cards */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="aspect-[4/5]">
                  <img
                    src={whoNeedsImages[serviceId] || "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=1000&fit=crop"}
                    alt="Who Needs This"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/20 to-transparent" />
              </div>

              {/* Floating Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-6 left-6 right-6 bg-white rounded-2xl p-5 shadow-2xl"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-primary" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">Deep Industry</div>
                      <div className="text-sm text-gray-500">Knowledge</div>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-gray-200" />
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-primary" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-base font-bold text-gray-900">Proven</div>
                      <div className="text-sm text-gray-500">Expertise</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach - Quote Banner */}
      <section
        className="relative py-24 px-6 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgb(21, 90, 48) 0%, rgb(13, 61, 31) 100%)",
        }}
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Approach Tag */}
            <div className="inline-block px-6 py-2.5 bg-white/15 backdrop-blur-sm rounded-full mb-8 border border-white/20">
              <span className="text-sm font-bold text-white uppercase tracking-widest">
                {service.approach.title}
              </span>
            </div>

            {/* Quote with Visual Mark */}
            <div className="relative">
              {/* Left Quote Mark */}
              <div className="absolute -left-4 top-0 text-6xl text-white/20 leading-none italic" style={{ fontFamily: 'Georgia, serif' }}>"</div>
              
              <blockquote 
                className="text-2xl md:text-3xl lg:text-2xl text-white leading-relaxed font-normal px-8 italic"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                {service.approach.quote}
              </blockquote>
              
              {/* Right Quote Mark */}
              <div className="absolute -right-4 bottom-0 text-6xl text-white/20 leading-none italic" style={{ fontFamily: 'Georgia, serif' }}>"</div>
            </div>

            {/* Decorative Divider */}
            <div className="mt-12 flex items-center justify-center gap-3">
              <div className="w-12 h-0.5 bg-white/30 rounded-full" />
              <div className="w-2 h-2 bg-white rounded-full" />
              <div className="w-12 h-0.5 bg-white/30 rounded-full" />
            </div>
          </motion.div>
        </div>
      </section>
{/* Expertise - Enhanced Timeline with Animated Progress Line */}
      <section className="py-20 md:py-24 px-6 md:px-8 lg:px-12 bg-gradient-to-b from-[#F6F4EF] to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" 
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
        </div>

        {/* Decorative Blob */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.05, 0.03]
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"
        />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-block px-6 py-2 bg-primary/10 rounded-full mb-4">
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Our Expertise</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-[1.1] mb-4"
              style={{ letterSpacing: '-0.02em' }}
            >
              <span className="inline-block relative pb-3">
                {service.expertise.title}
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1 bg-primary rounded-full"></span>
              </span>
            </h2>
          </motion.div>

          {/* Expertise Items - Alternating Layout with Animated Timeline */}
          <div className="relative">
            {/* Static Background Line - Hidden on mobile */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 transform -translate-x-1/2" />

            {/* Animated Progress Line - Grows on scroll */}
            <motion.div
              className="hidden lg:block absolute left-1/2 top-0 w-1 bg-gradient-to-b from-primary via-primary to-primary transform -translate-x-1/2 rounded-full shadow-lg"
              style={{
                boxShadow: '0 0 20px rgba(27, 120, 64, 0.5)'
              }}
              initial={{ height: '0%' }}
              whileInView={{ height: '100%' }}
              viewport={{ once: false, amount: 0.1 }}
              transition={{ 
                duration: 1.5, 
                ease: [0.22, 1, 0.36, 1]
              }}
            />

            {/* Animated Dot at the end of progress line */}
            <motion.div
              className="hidden lg:block absolute left-1/2 bottom-0 w-4 h-4 bg-primary rounded-full transform -translate-x-1/2 shadow-xl"
              style={{
                boxShadow: '0 0 20px rgba(27, 120, 64, 0.8)'
              }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ 
                duration: 0.5,
                delay: 1.5
              }}
            >
              {/* Pulsing ring around the dot */}
              <motion.div
                className="absolute inset-0 rounded-full bg-primary"
                animate={{
                  scale: [1, 2, 2],
                  opacity: [0.6, 0, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
              />
            </motion.div>

            <div className="space-y-16 lg:space-y-24">
              {service.expertise.expertiseAreas.map((area, index) => {
                const isLeft = index % 2 === 0;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className={`flex flex-col lg:flex-row gap-8 items-center ${!isLeft ? 'lg:flex-row-reverse' : ''}`}>
                      {/* Number Circle - Center on desktop, left on mobile */}
                      <div className="relative lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 flex-shrink-0 z-20">
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ 
                            duration: 0.5, 
                            delay: 0.2,
                            type: "spring",
                            stiffness: 200
                          }}
                          whileHover={{ scale: 1.15, rotate: 360 }}
                          className="relative"
                        >
                          {/* Outer Ring Animation */}
                          <motion.div
                            animate={{ 
                              scale: [1, 1.3, 1],
                              opacity: [0.4, 0, 0.4]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute inset-0 rounded-full bg-primary blur-lg"
                          />
                          
                          {/* Number Circle - FIXED: Changed text-black to text-white */}
                          <div className="relative w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                            <span className="text-3xl md:text-4xl font-bold text-black">{index + 1}</span>
                          </div>
                        </motion.div>
                      </div>

                      {/* Content Card */}
                      <motion.div
                        whileHover={{ y: -5, scale: 1.02 }}
                        className="flex-1 w-full lg:w-[calc(50%-3rem)]"
                      >
                        <div className="group relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-primary/20">
                          {/* Corner Badge */}
                          <div className="absolute -top-3 -right-3 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Award className="w-6 h-6 text-primary" strokeWidth={2} />
                          </div>

                          {/* Icon Badge */}
                          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                            <Sparkles className="w-8 h-8 text-primary" strokeWidth={2} />
                          </div>

                          {/* Title */}
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">
                            {area.title}
                          </h3>

                          {/* Description */}
                          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                            {area.description}
                          </p>

                          {/* Bottom Accent */}
                          <motion.div
                            className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary to-primary-dark rounded-b-2xl"
                            initial={{ width: '0%' }}
                            whileInView={{ width: '100%' }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                          />

                          {/* Side Accent Line */}
                          <div className={`absolute top-0 ${isLeft ? 'right-0' : 'left-0'} w-1 h-full bg-gradient-to-b from-primary via-primary/50 to-transparent rounded-${isLeft ? 'r' : 'l'}-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                        </div>
                      </motion.div>

                      {/* Spacer for alignment on desktop */}
                      <div className="hidden lg:block flex-1 w-[calc(50%-3rem)]" />
                    </div>

                    {/* Connector Line to Next Item - Desktop Only */}
                    {index < service.expertise.expertiseAreas.length - 1 && (
                      <motion.div
                        initial={{ scaleY: 0, opacity: 0 }}
                        whileInView={{ scaleY: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="hidden lg:block absolute left-1/2 bottom-0 w-0.5 h-16 bg-gradient-to-b from-primary/50 to-transparent transform -translate-x-1/2 translate-y-full origin-top"
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Bottom Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 p-10 bg-gradient-to-br from-white to-gray-50 rounded-3xl border-2 border-primary/20 shadow-xl"
          >
            <div className="grid md:grid-cols-3 gap-8 text-center">
              {[
                { icon: TrendingUp, label: 'Projects Delivered', value: '15+' },
                { icon: Users, label: 'Team Members', value: '10+' },
                { icon: Shield, label: 'Years of Excellence', value: '4+' }
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="group"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4 group-hover:bg-primary transition-colors">
                      <Icon className="w-8 h-8 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                    </div>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                    <div className="text-sm text-gray-600 font-medium uppercase tracking-wider">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs Section - Only show if there's a matching FAQ service */}
      {faqServiceName && <ServiceFAQs service={faqServiceName} showAll={false} />}

 {/* Success CTA - Elegant Quote Style */}
      <section
        className="relative py-24 md:py-32 px-6 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgb(21, 90, 48) 0%, rgb(13, 61, 31) 100%)",
        }}
      >
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Success Tag */}
            <div className="text-center mb-8">
              <div className="inline-block px-6 py-2.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
                <span className="text-sm font-bold text-white uppercase tracking-widest">
                  Ready to Transform Your Business?
                </span>
              </div>
            </div>

            {/* Main Heading */}
            <div className="relative text-center mb-12">
              <h2 
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white leading-tight font-extrabold mb-4"
                style={{ letterSpacing: '-0.02em' }}
              >
                {service.noteOfSuccess.title}
              </h2>
              
              {/* Decorative underline */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '120px' }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="h-1 bg-white/40 rounded-full mx-auto mb-8"
              />

              <p 
                className="text-lg md:text-xl text-white/95 leading-relaxed max-w-3xl mx-auto"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                {service.noteOfSuccess.description}
              </p>
            </div>

            {/* CTA Buttons - Refined Style */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <motion.a
                href={`${config.contact.whatsappLink}?text=Hi, I'm interested in ${service.hero.h1} service.`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="group px-8 py-4 bg-white hover:bg-gray-50 text-primary font-bold text-lg rounded-full shadow-xl transition-all flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                Start WhatsApp Chat
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
              </motion.a>

              <motion.a
                href="/Contact"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-bold text-lg rounded-full border-2 border-white/30 hover:border-white/50 transition-all flex items-center gap-3"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Book Consultation
              </motion.a>
            </div>

            {/* Trust Indicators - Elegant Divider Style */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-0.5 bg-white/30 rounded-full" />
              <div className="w-2 h-2 bg-white rounded-full" />
              <div className="w-12 h-0.5 bg-white/30 rounded-full" />
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-white/90">
              <motion.div 
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Shield className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="text-sm font-medium">100% Confidential</span>
              </motion.div>

              <motion.div 
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Award className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="text-sm font-medium">Happy Clients</span>
              </motion.div>

              <motion.div 
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Zap className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="text-sm font-medium">24/7 Support</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;